import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useQuery } from '@tanstack/react-query'

import { useAuth } from '../auth/AuthProvider'
import { parseClinicIdentity } from '../overview/overview-model'
import {
  firebaseOverviewRepository,
  type OverviewRepository,
} from '../overview/overview-repository'
import {
  firebaseTenantRepository,
  type Branch,
  type TenantRepository,
} from './tenant-repository'
import { authorizationTenantRepository } from './authorization-tenant-repository'

export type ClinicTenant = {
  id: string
  name: string
  relationship: 'caller' | 'branch'
}

type TenantContextValue = {
  status: 'idle' | 'loading' | 'ready' | 'error'
  callerClinic: ClinicTenant | null
  branches: ClinicTenant[]
  authorizedClinics: ClinicTenant[]
  selectedClinic: ClinicTenant | null
  selectClinic: (clinicId: string) => boolean
  refreshClinics: (preferredClinicId?: string) => void
  error: string | null
}

type ClinicIdentityRepository = Pick<OverviewRepository, 'getClinicInfo'>

const TenantContext = createContext<TenantContextValue | null>(null)
const SELECTED_CLINIC_STORAGE_PREFIX = 'flio.dashboard.selectedClinic.v1'

function selectedClinicStorageKey(callerUid: string) {
  return `${SELECTED_CLINIC_STORAGE_PREFIX}:${encodeURIComponent(callerUid)}`
}

function readSelectedClinicPreference(callerUid: string): string | null {
  try {
    const value = window.localStorage.getItem(
      selectedClinicStorageKey(callerUid)
    )
    return value && value.length <= 128 ? value : null
  } catch {
    return null
  }
}

function writeSelectedClinicPreference(
  callerUid: string,
  clinicId: string | null
) {
  try {
    const key = selectedClinicStorageKey(callerUid)
    if (clinicId) window.localStorage.setItem(key, clinicId)
    else window.localStorage.removeItem(key)
  } catch {
    // Browser storage is only a convenience preference. Server-authorized
    // clinic membership remains the sole authority when storage is blocked.
  }
}

function asClinicTenants(branches: Branch[]): ClinicTenant[] {
  return branches.map((branch) => ({
    ...branch,
    relationship: 'branch' as const,
  }))
}

function soleAuthorizedClinicId(authorizedIds: Set<string>) {
  if (authorizedIds.size !== 1) return null
  for (const clinicId of authorizedIds) return clinicId
  return null
}

function resolveClinicIdentityRepository(
  repository: TenantRepository,
  requested: ClinicIdentityRepository | null | undefined
) {
  if (requested !== undefined) return requested
  // Custom tenant repositories stay isolated unless their caller explicitly
  // supplies the matching metadata authority.
  return repository === firebaseTenantRepository ||
    repository === authorizationTenantRepository
    ? firebaseOverviewRepository
    : null
}

export function TenantProvider({
  children,
  repository = authorizationTenantRepository,
  clinicIdentityRepository,
  includeCallerClinic = true,
}: {
  children: ReactNode
  repository?: TenantRepository
  clinicIdentityRepository?: ClinicIdentityRepository | null
  includeCallerClinic?: boolean
}) {
  const auth = useAuth()
  const [status, setStatus] = useState<TenantContextValue['status']>('idle')
  const [branches, setBranches] = useState<ClinicTenant[]>([])
  const [callerClinicAvailable, setCallerClinicAvailable] = useState(false)
  const [selectedClinicId, setSelectedClinicId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [refreshRevision, setRefreshRevision] = useState(0)
  const preferredClinicId = useRef<string | null>(null)

  const callerUid = auth.status === 'authenticated' ? auth.user.uid : null
  const callerClinic = useMemo<ClinicTenant | null>(
    () =>
      callerUid && includeCallerClinic && callerClinicAvailable
        ? { id: callerUid, name: 'Your clinic', relationship: 'caller' }
        : null,
    [callerClinicAvailable, callerUid, includeCallerClinic]
  )

  useEffect(() => {
    let active = true

    setBranches([])
    setCallerClinicAvailable(false)
    setSelectedClinicId(null)
    setError(null)

    if (!callerUid) {
      setStatus('idle')
      return () => {
        active = false
      }
    }

    setStatus('loading')
    const callerClinicPromise =
      includeCallerClinic && repository.hasCallerClinic
        ? repository.hasCallerClinic(callerUid)
        : Promise.resolve(includeCallerClinic)
    void Promise.all([repository.getBranches(callerUid), callerClinicPromise])
      .then(([result, callerAvailable]) => {
        if (!active) return
        const resolvedBranches = asClinicTenants(
          result.filter((branch) => branch.id !== callerUid)
        )
        const authorizedIds = new Set([
          ...(callerAvailable ? [callerUid] : []),
          ...resolvedBranches.map((clinic) => clinic.id),
        ])
        const requestedPreference = preferredClinicId.current
        const persistedPreference = readSelectedClinicPreference(callerUid)
        if (persistedPreference && !authorizedIds.has(persistedPreference)) {
          writeSelectedClinicPreference(callerUid, null)
        }
        const soleClinicId = soleAuthorizedClinicId(authorizedIds)
        const nextSelectedClinicId =
          requestedPreference && authorizedIds.has(requestedPreference)
            ? requestedPreference
            : persistedPreference && authorizedIds.has(persistedPreference)
              ? persistedPreference
              : callerAvailable
                ? callerUid
                : soleClinicId
        setBranches(resolvedBranches)
        setCallerClinicAvailable(callerAvailable)
        setSelectedClinicId(nextSelectedClinicId)
        writeSelectedClinicPreference(callerUid, nextSelectedClinicId)
        preferredClinicId.current = null
        setStatus('ready')
      })
      .catch(() => {
        if (!active) return
        setBranches([])
        setCallerClinicAvailable(false)
        setSelectedClinicId(null)
        setError('Branch information could not be loaded.')
        setStatus('error')
      })

    return () => {
      active = false
    }
  }, [callerUid, includeCallerClinic, refreshRevision, repository])

  const authorizedClinics = useMemo(
    () => (callerClinic ? [callerClinic, ...branches] : branches),
    [branches, callerClinic]
  )

  const selectedClinicAuthority = useMemo(
    () =>
      authorizedClinics.find((clinic) => clinic.id === selectedClinicId) ??
      null,
    [authorizedClinics, selectedClinicId]
  )
  const resolvedClinicIdentityRepository = resolveClinicIdentityRepository(
    repository,
    clinicIdentityRepository
  )
  const clinicIdentity = useQuery({
    // This is the existing Overview identity key, so the shell and Overview
    // share one cache entry and one in-flight RTDB read.
    queryKey: ['overview', selectedClinicAuthority?.id ?? null, 'clinic'],
    queryFn: async () => {
      if (!selectedClinicAuthority || !resolvedClinicIdentityRepository) {
        return null
      }
      return parseClinicIdentity(
        await resolvedClinicIdentityRepository.getClinicInfo(
          selectedClinicAuthority.id
        )
      )
    },
    enabled:
      selectedClinicAuthority !== null &&
      resolvedClinicIdentityRepository !== null,
  })
  const selectedClinic = useMemo(() => {
    if (!selectedClinicAuthority) return null
    const authoritativeName = clinicIdentity.data?.name
    return authoritativeName
      ? { ...selectedClinicAuthority, name: authoritativeName }
      : selectedClinicAuthority
  }, [clinicIdentity.data?.name, selectedClinicAuthority])

  const selectClinic = useCallback(
    (clinicId: string) => {
      const allowed = authorizedClinics.some((clinic) => clinic.id === clinicId)
      if (allowed) {
        setSelectedClinicId(clinicId)
        if (callerUid) writeSelectedClinicPreference(callerUid, clinicId)
      }
      return allowed
    },
    [authorizedClinics, callerUid]
  )
  const refreshClinics = useCallback((preferred?: string) => {
    preferredClinicId.current = preferred ?? null
    setRefreshRevision((revision) => revision + 1)
  }, [])

  const value = useMemo<TenantContextValue>(
    () => ({
      status,
      callerClinic,
      branches,
      authorizedClinics,
      selectedClinic,
      selectClinic,
      refreshClinics,
      error,
    }),
    [
      authorizedClinics,
      branches,
      callerClinic,
      error,
      refreshClinics,
      selectClinic,
      selectedClinic,
      status,
    ]
  )

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  )
}

export function useTenant() {
  const context = useContext(TenantContext)
  if (!context) throw new Error('useTenant must be used inside TenantProvider')
  return context
}
